import { GenericExternalLink } from "@components/generic-external-link";
import { CopyTextButton } from "@components/v2/buttons/copy-text-button";
import { Body } from "@components/v2/typography/body";

const AWS_USER_ARN =
  "arn:aws:iam::892581138023:user/nb-data-orders-export-custom-sa-user";

export const S3ConfigurationInstructions = () => {
  return (
    <div className="flex flex-col gap-4 p-4 rounded-lg border border-v2-tertiary bg-v2-primary shadow-card h-fit">
      <div className="space-y-1">
        <Body variant={200} fontWeight="semibold">
          Grant bucket access to Northbeam
        </Body>
        <Body variant={300} fontWeight="regular">
          Learn more about the S3 permissions{" "}
          <GenericExternalLink href="https://docs.northbeam.io/docs/northbeam-api-data-export-s3-export-1">
            here
          </GenericExternalLink>
          .
        </Body>
      </div>
      <div className="space-y-1">
        <Body variant={200} color="secondary" fontWeight="medium">
          1. Role:
        </Body>
        <Body variant={300} fontWeight="regular">
          Create a role using a{" "}
          <span className="font-bold">Custom trust policy</span> adding
          Northbeam&apos;s AWS User as the Principal with the{" "}
          <code>sts:AssumeRole</code> permission
          <CopyTextButton
            textToCopy={AWS_TRUST_POLICY}
            successMessage="AWS trust policy copied to clipboard"
            className="mt-1 text-primary"
          >
            Copy full trust policy object
          </CopyTextButton>
        </Body>
        <Body variant={300} fontWeight="regular" className="underline">
          AWS User ARN:
        </Body>
        <CopyTextButton
          textToCopy={AWS_USER_ARN}
          successMessage="AWS User ARN copied to clipboard"
        />
      </div>
      <div className="space-y-1">
        <Body variant={200} color="secondary" fontWeight="medium">
          2. Policy:
        </Body>
        <Body variant={300} fontWeight="regular">
          Create a policy providing the permissions below, changing the
          placeholders <code>{"<BUCKET_NAME>"}</code> and{" "}
          <code>{"<ROLE_ARN>"}</code> by the ones you created
          <CopyTextButton
            textToCopy={AWS_POLICY_OBJECT}
            successMessage="AWS User ARN copied to clipboard"
            className="my-1 text-primary"
          >
            Copy full policy object
          </CopyTextButton>
        </Body>
        <div className="grid grid-cols-2">
          <div>
            <Body variant={300} fontWeight="regular" className="underline">
              Bucket
            </Body>
            <Body className="flex flex-col mb-1">
              Permissions
              <code>s3:PutObject</code>
              <code>s3:GetObject</code>
              <code>s3:ListBucket</code>
              <code>s3:DeleteObject</code>
            </Body>
            <Body className="flex flex-col mb-2">
              Resources
              <code>{"arn:aws:s3:::<BUCKET_NAME>"}</code>
              <code>{"arn:aws:s3:::<BUCKET_NAME>/*"}</code>
            </Body>
          </div>
          <div>
            <Body variant={300} fontWeight="regular" className="underline">
              Role
            </Body>
            <Body className="flex flex-col mb-1">
              Permissions
              <code>iam:SimulatePrincipalPolicy</code>
            </Body>
            <Body className="flex flex-col mb-1">
              Resources
              <code>{"<ROLE_ARN>"}</code>
            </Body>
          </div>
        </div>
      </div>
      <div className="space-y-1">
        <Body variant={200} color="secondary" fontWeight="medium">
          3. Attach policy to Role:
        </Body>
        <Body variant={300} fontWeight="regular">
          Attach the policy to the role you created.
        </Body>
      </div>
    </div>
  );
};

const AWS_TRUST_POLICY = `{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Sid": "Statement1",
      "Effect": "Allow",
      "Principal": {
        "AWS": "arn:aws:iam::892581138023:user/nb-data-orders-export-custom-sa-user"
      },
      "Action": "sts:AssumeRole"
    }
  ]
}`;

const AWS_POLICY_OBJECT = `{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Sid": "Statement1",
      "Effect": "Allow",
      "Action": [
        "s3:PutObject",
        "s3:GetObject",
        "s3:ListBucket",
        "s3:DeleteObject"
      ],
      "Resource": [
        "arn:aws:s3:::<BUCKET_NAME>", // Add your bucket name
        "arn:aws:s3:::<BUCKET_NAME>/*" // Add your bucket name
      ]
    },
    {
      "Sid": "Statement2",
      "Effect": "Allow",
      "Action": ["iam:SimulatePrincipalPolicy"],
      "Resource": ["<ROLE_ARN>"] // Add your role ARN
    }
  ]
}`;
